'use client';


import React, { useEffect } from 'react';
// ─── Tick-to-Render Latency Monitor ──────────────────────────────────────────
// Uses performance.mark() / performance.measure() to track the full pipeline:
//   tick-received  →  context-state-set  →  component-render-start  →  component-render-end
//
// Budget: < 200 ms end-to-end (tick received → DOM updated)

export type LatencyScreen = 'holdings' | 'eod-settlement';

export interface LatencyEntry {
  symbol: string;
  screen: LatencyScreen;
  tickReceivedAt: number;   // performance.now() when tick arrived in context
  renderStartAt: number;    // performance.now() when component started re-render
  renderEndAt: number;      // performance.now() when component finished painting
  tickToRender: number;     // renderEnd - tickReceived  (the key metric)
  renderDuration: number;   // renderEnd - renderStart
  overBudget: boolean;      // tickToRender > BUDGET_MS
}

export interface LatencySummary {
  screen: LatencyScreen;
  count: number;
  p50: number;
  p75: number;
  p95: number;
  p99: number;
  max: number;
  overBudgetCount: number;
  overBudgetPct: number;
}

const BUDGET_MS = 200;
const MAX_ENTRIES = 500; // ring-buffer cap to avoid memory growth

// ─── In-memory ring buffer ────────────────────────────────────────────────────

const entries: LatencyEntry[] = [];

function addEntry(entry: LatencyEntry) {
  if (entries.length >= MAX_ENTRIES) entries.shift();
  entries.push(entry);
  emitToListeners(entry);
}

// ─── Listeners (for live overlay) ────────────────────────────────────────────

type LatencyListener = (entry: LatencyEntry) => void;
const listeners = new Set<LatencyListener>();

export function subscribeLatency(fn: LatencyListener): () => void {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

function emitToListeners(entry: LatencyEntry) {
  listeners.forEach((fn) => fn(entry));
}

// ─── Mark helpers ─────────────────────────────────────────────────────────────

/** Called in MarketDataContext when a tick is received (Supabase RT or simulation) */
export function markTickReceived(symbol: string): number {
  if (typeof performance === 'undefined') return 0;
  const t = performance.now();
  try {
    performance.mark(`tick:received:${symbol}`, { detail: { symbol, t } });
  } catch {
    // Safari < 16.4 doesn't support detail option — silently ignore
  }
  return t;
}

/** Called at the top of a component that consumes tick data, before render work */
export function markRenderStart(symbol: string, screen: LatencyScreen): number {
  if (typeof performance === 'undefined') return 0;
  const t = performance.now();
  try {
    performance.mark(`tick:render-start:${symbol}:${screen}`);
  } catch { /* noop */ }
  return t;
}

/** Called in useEffect (after paint) to close the measurement */
export function markRenderEnd(
  symbol: string,
  screen: LatencyScreen,
  tickReceivedAt: number,
  renderStartAt: number
): void {
  if (typeof performance === 'undefined') return;
  const renderEndAt = performance.now();

  try {
    performance.mark(`tick:render-end:${symbol}:${screen}`);
    performance.measure(
      `tick-to-render:${symbol}:${screen}`,
      `tick:received:${symbol}`,
      `tick:render-end:${symbol}:${screen}`
    );
  } catch { /* marks may not exist if called out of order */ }

  if (tickReceivedAt === 0) return; // no valid start time

  const tickToRender = renderEndAt - tickReceivedAt;
  const renderDuration = renderEndAt - renderStartAt;

  const entry: LatencyEntry = {
    symbol,
    screen,
    tickReceivedAt,
    renderStartAt,
    renderEndAt,
    tickToRender,
    renderDuration,
    overBudget: tickToRender > BUDGET_MS,
  };

  addEntry(entry);

  // Warn in dev when over budget
  if (process.env.NODE_ENV === 'development' && entry.overBudget) {
    console.warn(
      `[TickLatency] ⚠ OVER BUDGET ${symbol} on ${screen}: ${tickToRender.toFixed(1)}ms (budget ${BUDGET_MS}ms)`
    );
  }
}

// ─── Analytics ────────────────────────────────────────────────────────────────

function percentile(sorted: number[], p: number): number {
  if (sorted.length === 0) return 0;
  const idx = Math.ceil((p / 100) * sorted.length) - 1;
  return parseFloat(sorted[Math.max(0, idx)].toFixed(2));
}

export function getLatencySummary(screen: LatencyScreen): LatencySummary {
  const screenEntries = entries.filter((e) => e.screen === screen);
  const values = screenEntries.map((e) => e.tickToRender).sort((a, b) => a - b);
  const overBudgetCount = screenEntries.filter((e) => e.overBudget).length;

  return {
    screen,
    count: values.length,
    p50: percentile(values, 50),
    p75: percentile(values, 75),
    p95: percentile(values, 95),
    p99: percentile(values, 99),
    max: values.length > 0 ? parseFloat(values[values.length - 1].toFixed(2)) : 0,
    overBudgetCount,
    overBudgetPct: values.length > 0 ? parseFloat(((overBudgetCount / values.length) * 100).toFixed(1)) : 0,
  };
}

export function getAllEntries(): LatencyEntry[] {
  return [...entries];
}

export function clearEntries(): void {
  entries.length = 0;
}

export { BUDGET_MS };
